/*
 * This unpublished material is proprietary to Vaticle.
 * All rights reserved. The methods and
 * techniques described herein are considered trade secrets
 * and/or confidential. Reproduction or distribution, in whole
 * or in part, is forbidden except by express written permission
 * of Vaticle.
 */

import { Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { ButtonComponent, PropertiesTableComponent, PropertiesTableRowComponent, SpinnerComponent } from "typedb-cloud-framework";
import { Cluster } from "../../../concept/cluster";
import { ApplicationState } from "../../../service/application-state.service";
import { ClusterApi } from "../../../service/cluster/cluster-api.service";
import { SpaceApi } from "../../../service/space/space-api.service";
import { ResourceService } from "../../../service/resource.service";
import { SnackbarService } from "../../../service/snackbar.service";
import { PageScaffoldComponent, ResourceAvailability } from "../../scaffold/page/page-scaffold.component";
import {
    BehaviorSubject, Observable, Subject, catchError, combineLatest, combineLatestWith, distinctUntilChanged, filter,
    first, interval, map, of, shareReplay, startWith, switchMap, takeUntil, tap
} from "rxjs";
import { AsyncPipe } from "@angular/common";
import { ServerStatusPipe } from "../server-status.pipe";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { DownloadClusterServerLogsData, DownloadClusterServerLogsDialogComponent } from "./download-logs-dialog/download-cluster-server-logs-dialog.component";
import { ServerLogsViewingPaneComponent } from "./logs-viewing-pane/server-logs-viewing-pane.component";
import { MatButtonModule } from "@angular/material/button";
import { LogsControlBarComponent } from "../log/logs-control-bar/logs-control-bar.component";

@Component({
    selector: "tp-server-details-page",
    templateUrl: "./server-details-page.component.html",
    styleUrls: ["./server-details-page.component.scss"],
    imports: [
        PageScaffoldComponent, PropertiesTableComponent, PropertiesTableRowComponent, AsyncPipe,
        SpinnerComponent, ServerStatusPipe, MatTooltipModule, ServerLogsViewingPaneComponent,
        RouterLink, MatButtonModule, LogsControlBarComponent
    ]
})
export class ServerDetailsPageComponent implements OnInit, OnDestroy {
    @ViewChild("logViewer") logViewerEl?: ElementRef<HTMLPreElement>;
    readonly cluster$ = new BehaviorSubject<Cluster | null>(null);
    readonly serverId$ = this.route.params.pipe(map(params => Number(params["server-id"])));
    readonly server$ = this.cluster$.pipe(
        takeUntilDestroyed(),
        filter(cluster => !!cluster),
        map(cluster => cluster!),
        switchMap((cluster) => interval(5000).pipe(
            takeUntilDestroyed(this.destroyRef),
            startWith(-1),
            switchMap(() => this.clusterApi.listServers(cluster.uuid)),
            combineLatestWith(this.serverId$),
            map(([servers, id]) => servers?.find(x => x.id === id)),
        )),
        shareReplay(1),
    );
    private readonly unsub$ = new Subject<void>();
    readonly availability$: Observable<ResourceAvailability> = combineLatest([this.cluster$, this.server$]).pipe(
        map(([x, y]) => {
            if (x?.status === "paused") return "failed"
            else return x && y ? "ready" : "loading"
        }), catchError(() => of("failed" as const))
    );
    isStreamingEnabled = true;

    constructor(
        private route: ActivatedRoute, private app: ApplicationState, private destroyRef: DestroyRef,
        private clusterApi: ClusterApi, private spaceApi: SpaceApi, private resourceService: ResourceService,
        private snackbar: SnackbarService, private dialog: MatDialog,
    ) {
        this.availability$.subscribe((availability) => {
            if (availability === "failed") this.snackbar.errorPersistent("Could not retrieve server. Please make sure your cluster is running.")
        });
    }

    ngOnInit() {
        this.initDataLoader();
    }

    ngOnDestroy() {
        this.unsub$.next();
    }

    private initDataLoader() {
        this.route.params.pipe(
            filter((params) => params["space-id"] && params["cluster-id"]),
            map((params) => ({ spaceId: params["space-id"] as string, clusterId: params["cluster-id"] as string })),
            distinctUntilChanged((a, b) => a.clusterId === b.clusterId && a.spaceId === b.spaceId),
            tap(() => { this.unsub$.next(); }),
            switchMap(({ spaceId, clusterId }) => {
                return this.spaceApi.getSpace({ spaceId, teamUuid: this.app.requireCurrentTeam().uuid }, of(undefined)).pipe(
                    first(),
                    map((space) => ({ space: space.initial!, clusterId })),
                );
            }),
            switchMap(({ space, clusterId }) => {
                return this.clusterApi.getCluster({ clusterId: clusterId, spaceUuid: space.uuid }, this.unsub$);
            }),
        ).subscribe({
            next: (res) => {
                this.resourceService.processResponse({ resource$: this.cluster$, res: res, unsub$: this.unsub$, onResync: () => this.initDataLoader() });
            },
            error: (err) => {
                this.cluster$.error(err);
            }
        });
    }

    openDownloadLogsDialog() {
        this.server$.pipe(first()).subscribe((server) => {
            this.dialog.open<DownloadClusterServerLogsDialogComponent, DownloadClusterServerLogsData>(
                DownloadClusterServerLogsDialogComponent,
                { data: { cluster: this.cluster$.value!, server: server! } }
            );
        });
    }

    copyServerAddress() {
        this.server$.pipe(
            filter(x => !!x?.address),
            map(x => x!.address!),
            first()
        ).subscribe((address) => {
            navigator.clipboard.writeText(address);
            this.snackbar.success("Copied", { duration: 1250 });
        })
    }
}
